import React from "react";
import styled from "styled-components";
import Button from "../Button";

interface SessionWarningModalProps {
  isOpen: boolean;
  remainingTime: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

export const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  isOpen,
  remainingTime,
  onExtendSession,
  onLogout,
}) => {
  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={onExtendSession} />
      <ModalContainer>
        <ModalContent>
          <WarningIcon>⚠️</WarningIcon>
          <Title>Session Expiring Soon</Title>
          <Message>Your session will expire due to inactivity. You will be automatically logged out in:</Message>
          <Timer>{formatTime(remainingTime)}</Timer>
          <InfoText>Click &ldquo;Stay Logged In&rdquo; to continue your session.</InfoText>
          <ButtonContainer>
            <Button onClick={onExtendSession} variant="primary" text="Stay Logged In" />
            <Button onClick={onLogout} variant="secondary" text="Log Out Now" />
          </ButtonContainer>
        </ModalContent>
      </ModalContainer>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  max-width: 450px;
  text-align: center;
`;

const WarningIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const Timer = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #e53e3e;
  margin-bottom: 1rem;
  font-family: monospace;
`;

const InfoText = styled.p`
  font-size: 0.9rem;
  color: #888;
  margin-bottom: 1.5rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;
